import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, ClipboardList, Hammer, Rocket, Search, TrendingUp } from 'lucide-react';
import { LAUNCH_PROCESS } from '@/data/launch';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const ACCENT = '#4225C4';
const ACCENT_SOFT = '#E4E0FA';

const STAGE_ICONS = {
  search: Search,
  'clipboard-list': ClipboardList,
  hammer: Hammer,
  rocket: Rocket,
  'trending-up': TrendingUp,
};

const StageCard = ({ stage, align, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const reduce = useReducedMotion();
  const Icon = STAGE_ICONS[stage.icon];
  const listId = `process-includes-${stage.id}`;

  return (
    <div
      data-testid={`process-stage-${stage.id}`}
      className={cn(
        'rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-shadow duration-300 hover:shadow-card sm:p-8',
        align === 'left' ? 'lg:mr-10' : 'lg:ml-10'
      )}
    >
      <div className="flex items-center gap-4">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white"
          style={{ backgroundColor: ACCENT }}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <span className="font-mono text-xs font-medium tracking-widest" style={{ color: ACCENT }}>
            Phase {stage.number}
          </span>
          <h3 className="mt-0.5 text-xl font-extrabold tracking-tight text-navy-900">{stage.title}</h3>
        </div>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{stage.text}</p>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        data-testid={`process-toggle-${stage.id}`}
        className="group mt-5 inline-flex items-center gap-2 text-sm font-bold transition-colors"
        style={{ color: ACCENT }}
      >
        What’s included
        <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={listId}
            initial={reduce ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={reduce ? undefined : { opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <ul className="mt-4 flex flex-wrap gap-2.5 border-t border-slate-100 pt-4">
              {stage.includes.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold text-navy-800"
                  style={{ backgroundColor: ACCENT_SOFT }}
                >
                  <Check className="h-3 w-3" style={{ color: ACCENT }} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ProcessJourney = () => (
  <section aria-labelledby="process-heading" data-testid="process-section" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center" data-testid="process-header">
        <p className="flex items-center justify-center gap-3">
          <span className="font-mono text-xs font-medium tracking-widest" style={{ color: ACCENT }}>04</span>
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
            {LAUNCH_PROCESS.eyebrow}
          </span>
        </p>
        <h2 id="process-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
          {LAUNCH_PROCESS.heading}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{LAUNCH_PROCESS.intro}</p>
      </div>

      <div className="relative mx-auto mt-16 max-w-5xl">
        <span className="absolute bottom-8 left-[22px] top-2 w-0.5 bg-navy-200 lg:left-1/2 lg:-translate-x-1/2" aria-hidden="true" />
        <ol className="space-y-10 lg:space-y-14">
          {LAUNCH_PROCESS.stages.map((stage, i) => {
            const left = i % 2 === 0;
            return (
              <li key={stage.id} className="relative lg:grid lg:grid-cols-2">
                <span
                  className="absolute left-[22px] top-7 z-10 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border-4 border-mist bg-navy-900 font-mono text-xs font-bold text-white lg:left-1/2"
                  aria-hidden="true"
                >
                  {stage.number}
                </span>
                <Reveal className={cn('pl-14 lg:pl-0', left ? 'lg:col-start-1' : 'lg:col-start-2')}>
                  <StageCard stage={stage} align={left ? 'left' : 'right'} defaultOpen={i === 0} />
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  </section>
);
